'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import styles from './ActivitiesShowcase.module.scss';
import { Container } from '@mantine/core';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import PresentationOfSection from '../Atoms/PresentationOfSection/PresentationOfSection';
import { refreshAOS, useAOS } from '@/src/lib/hooks/useAOS';

export type StoryItem = {
    id: string;
    title: string;
    description: string;
    imageSrc: string;
    imageAlt?: string;
    href: string;
    tag?: string;
    badgeSecondary?: string;
    nextSession?: string;
    ctaLabel?: string;
    themeHex?: string;
    textColor?: string;
};

type BookingProduct = {
    _id: string;
    title: string;
    slug: string;
    type?: string;
    isActive?: boolean;
    ageMin?: number;
    durationMin?: number;
    capacityPerSlot?: number;
    publicDescription?: string;
    imageUrl?: string;
    pricing?: {
        mode?: 'fixed' | 'variants';
        price?: number;
        variants?: Array<{
            id: string;
            label: string;
            price: number;
        }>;
        options?: Array<{
            id: string;
            label: string;
            price: number;
        }>;
    };
    schedule?: Array<{
        weekday: number;
        isClosed?: boolean;
        slots?: Array<{ startTime: string; endTime: string }>;
    }>;
};

type CrudResponse<T> = {
    items: T[];
    total: number;
    page: number;
    perPage: number;
};

type RdvSlotResponse = {
    today: string;
    items: RdvSlotItem[];
};

type RdvSlotItem = {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    capacity: number;
    pricePerPersonCents: number;
    activityIds: string[];
    activityTitles: string[];
    imageUrl: string | null;
};

type ActivitiesSource =
    | {
          type: 'booking_products';
          limit?: number;
          onlyActive?: boolean;
          themeHex?: string;
          textColor?: string;
          baseHref?: string;
      }
    | {
          type: 'rdv_slots';
          limit?: number;
          themeHex?: string;
          textColor?: string;
          baseHref?: string;
          cleanup?: boolean;
          tagLabel?: string;
      };

export type ActivitiesShowcaseSlice = {
    type: 'activities-showcase';
    eyebrow?: string;
    title?: string;
    subtitle?: string;
    items?: StoryItem[];
    className?: string;
    themeMode?: 'single' | 'perCard';
    singleThemeHex?: string;
    textColor?: string;
    source?: ActivitiesSource;
};

function normalizeHex(input?: string) {
    const v = (input ?? '').trim();
    if (!v) return '';
    return v.startsWith('#') ? v : `#${v}`;
}

function hexToRgb(hexInput?: string): { r: number; g: number; b: number } | null {
    const hex = normalizeHex(hexInput).replace('#', '');

    if (![3, 6].includes(hex.length)) {
        return null;
    }

    const full =
        hex.length === 3
            ? hex
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : hex;

    const int = Number.parseInt(full, 16);

    if (Number.isNaN(int)) {
        return null;
    }

    return {
        r: (int >> 16) & 255,
        g: (int >> 8) & 255,
        b: int & 255,
    };
}

function cleanText(value?: string) {
    return (value ?? '').replace(/\s+/g, ' ').trim();
}

function slugify(value?: string) {
    return (value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function formatPriceEuros(value?: number | null) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return null;
    }

    return `${value}€ TTC`;
}

function formatPriceFromCents(cents?: number | null) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) {
        return null;
    }

    return `${Math.round(cents / 100)}€ / pers`;
}

function formatDateFr(ymd?: string) {
    if (!ymd) {
        return '';
    }

    const [year, month, day] = ymd.split('-').map(Number);

    if (!year || !month || !day) {
        return ymd;
    }

    const date = new Date(year, month - 1, day);

    return new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
    }).format(date);
}

function parseHour(time?: string) {
    if (!time) {
        return 0;
    }

    const [h] = time.split(':').map(Number);

    return Number.isFinite(h) ? h : 0;
}

function getPeriodFromStartTime(startTime?: string) {
    const hour = parseHour(startTime);
    return hour < 13 ? 'Matin' : 'Après-midi';
}

function getBookingStartingPrice(product: BookingProduct): number | null {
    if (!product.pricing) {
        return null;
    }

    if (product.pricing.mode === 'fixed' && typeof product.pricing.price === 'number') {
        return product.pricing.price;
    }

    if (
        product.pricing.mode === 'variants' &&
        Array.isArray(product.pricing.variants) &&
        product.pricing.variants.length
    ) {
        const prices = product.pricing.variants
            .map((variant) => variant.price)
            .filter((price) => typeof price === 'number');

        if (!prices.length) {
            return null;
        }

        return Math.min(...prices);
    }

    return null;
}

function getBookingNextSessionText(product: BookingProduct) {
    const hasAnyOpenDay = Array.isArray(product.schedule)
        ? product.schedule.some(
              (day) => !day.isClosed && Array.isArray(day.slots) && day.slots.length > 0
          )
        : false;

    return hasAnyOpenDay ? 'Créneaux disponibles' : 'Sur réservation';
}

function buildBookingDescription(product: BookingProduct) {
    const parts: string[] = [];

    if (typeof product.durationMin === 'number') {
        parts.push(`${product.durationMin} min`);
    }

    if (typeof product.ageMin === 'number') {
        parts.push(`Dès ${product.ageMin} ans`);
    }

    const price = formatPriceEuros(getBookingStartingPrice(product));

    if (price) {
        parts.push(`À partir de ${price}`);
    }

    const description = cleanText(product.publicDescription);

    if (description) {
        parts.push(description);
    }

    return parts.join(' • ');
}

async function fetchAllPages<T>(collection: string): Promise<T[]> {
    const results: T[] = [];
    let page = 1;
    let totalPages = 1;

    do {
        const res = await fetch(`/api/admin/crud/${collection}?page=${page}`, {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store',
        });

        if (!res.ok) {
            throw new Error(`Failed to fetch collection "${collection}"`);
        }

        const data: CrudResponse<T> = await res.json();

        results.push(...(data.items ?? []));

        totalPages = Math.max(1, Math.ceil((data.total ?? 0) / (data.perPage || 20)));
        page += 1;
    } while (page <= totalPages);

    return results;
}

async function fetchRdvSlots(cleanup = true): Promise<RdvSlotItem[]> {
    const res = await fetch(`/api/booking/rdv/slots?cleanup=${cleanup ? '1' : '0'}`, {
        method: 'GET',
        cache: 'no-store',
    });

    if (!res.ok) {
        throw new Error('Failed to fetch RDV slots');
    }

    const data: RdvSlotResponse = await res.json();

    return data.items ?? [];
}

function mapBookingProductsToItems(
    products: BookingProduct[],
    source: Extract<ActivitiesSource, { type: 'booking_products' }>,
    sliceTextColor?: string
): StoryItem[] {
    const filtered = products.filter((product) => {
        if (source.onlyActive === false) {
            return true;
        }

        return product.isActive;
    });

    return filtered.slice(0, source.limit ?? 8).map((product) => ({
        id: product._id,
        title: product.title,
        description: buildBookingDescription(product),
        imageSrc: product.imageUrl || '/images/placeholder.jpg',
        imageAlt: product.title,
        href: `${source.baseHref ?? '/fr/activities'}#${product.slug || slugify(product.title)}`,
        ctaLabel: 'Réserver',
        nextSession: getBookingNextSessionText(product),
        themeHex: source.themeHex ?? '#957365',
        textColor: source.textColor ?? sliceTextColor,
        tag: product.type ? product.type.charAt(0).toUpperCase() + product.type.slice(1) : undefined,
    }));
}

function mapRdvSlotsToItems(
    slots: RdvSlotItem[],
    source: Extract<ActivitiesSource, { type: 'rdv_slots' }>,
    sliceTextColor?: string
): StoryItem[] {
    return [...slots]
        .sort((a, b) => {
            if (a.date !== b.date) {
                return a.date.localeCompare(b.date);
            }

            return a.startTime.localeCompare(b.startTime);
        })
        .slice(0, source.limit ?? 8)
        .map((slot) => {
            const title =
                Array.isArray(slot.activityTitles) && slot.activityTitles.length
                    ? slot.activityTitles.join(' • ')
                    : 'Atelier RDV';

            const descriptionParts: string[] = [
                `${slot.startTime}–${slot.endTime}`,
                `${slot.capacity} places`,
            ];

            const price = formatPriceFromCents(slot.pricePerPersonCents);

            if (price) {
                descriptionParts.push(price);
            }

            return {
                id: slot.id,
                title,
                description: descriptionParts.join(' • '),
                imageSrc: slot.imageUrl || '/images/placeholder.jpg',
                imageAlt: title,
                href: `${source.baseHref ?? '/rdv-activities'}?date=${slot.date}&start=${slot.startTime}`,
                ctaLabel: 'Réserver',
                nextSession: formatDateFr(slot.date),
                themeHex: source.themeHex ?? '#957365',
                textColor: source.textColor ?? sliceTextColor,
                tag: getPeriodFromStartTime(slot.startTime),
                badgeSecondary: formatDateFr(slot.date),
            };
        });
}

export default function ActivitiesShowcase({ slice }: { slice: ActivitiesShowcaseSlice }) {
    const { title, subtitle, className } = slice;
    const dictionary = useDictionary();

    const [dynamicItems, setDynamicItems] = useState<StoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const themeMode = slice.themeMode ?? 'perCard';
    const singleRgb = hexToRgb(slice.singleThemeHex ?? '#957365') ?? {
        r: 149,
        g: 115,
        b: 101,
    };
    const fallbackTextRgb = hexToRgb(slice.textColor ?? '#FFFFFF') ?? {
        r: 255,
        g: 255,
        b: 255,
    };

    useAOS();

    useEffect(() => {
        let cancelled = false;

        async function loadDynamicItems() {
            if (!slice.source) {
                setDynamicItems([]);
                return;
            }

            try {
                setLoading(true);
                setHasError(false);

                if (slice.source.type === 'booking_products') {
                    const products = await fetchAllPages<BookingProduct>('booking_products');

                    if (cancelled) {
                        return;
                    }

                    setDynamicItems(mapBookingProductsToItems(products, slice.source, slice.textColor));
                    return;
                }

                if (slice.source.type === 'rdv_slots') {
                    const slots = await fetchRdvSlots(slice.source.cleanup ?? true);

                    if (cancelled) {
                        return;
                    }

                    setDynamicItems(mapRdvSlotsToItems(slots, slice.source, slice.textColor));
                    return;
                }
            } catch (error) {
                console.error('ActivitiesShowcase dynamic fetch error:', error);

                if (!cancelled) {
                    setHasError(true);
                    setDynamicItems([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadDynamicItems();

        return () => {
            cancelled = true;
        };
    }, [slice.source, slice.textColor]);

    const items = useMemo(() => {
        if (slice.source) {
            return dynamicItems;
        }

        return (slice.items ?? []).map((item) => ({
            ...item,
            textColor: item.textColor ?? slice.textColor,
        }));
    }, [slice.source, slice.items, dynamicItems, slice.textColor]);

    /**
     * Important:
     * When the cards come from an async source, they are rendered after AOS.init().
     * This refresh makes sure mobile cards/title do not stay hidden by AOS CSS.
     */
    useEffect(() => {
        if (!loading && items.length > 0) {
            refreshAOS();
        }
    }, [loading, items.length]);

    const sectionClass = useMemo(
        () => [styles.section, className].filter(Boolean).join(' '),
        [className]
    );

    if (!loading && !items?.length) {
        return null;
    }

    return (
        <section className={sectionClass}>
            <Container size="xl" className={styles.container} mb="xl">
                <PresentationOfSection
                    title={title}
                    description={subtitle}
                    align="center"
                    aos={{ animation: 'fade-up', delay: 0 }}
                />

                {hasError && (
                    <p className={styles.feedback}>
                        Impossible de charger les activités pour le moment.
                    </p>
                )}

                <div className={styles.grid} role="list" aria-busy={loading}>
                    {loading
                        ? Array.from({ length: 6 }).map((_, idx) => (
                              <article
                                  key={`skeleton-${idx}`}
                                  className={`${styles.card} ${styles.skeletonCard}`}
                                  role="listitem"
                              >
                                  <div className={styles.stack}>
                                      <div className={`${styles.media} ${styles.skeletonBlock}`} />

                                      <div className={styles.seam} aria-hidden="true">
                                          <span
                                              className={`${styles.circleCta} ${styles.skeletonCircle}`}
                                          >
                                              <span className={styles.circleIcon}>→</span>
                                          </span>
                                      </div>

                                      <div
                                          className={`${styles.textCard} ${styles.skeletonTextCard}`}
                                      >
                                          <div
                                              className={`${styles.skeletonLine} ${styles.skeletonTitle}`}
                                          />
                                          <div className={styles.skeletonLine} />
                                          <div className={styles.skeletonLine} />
                                          <div
                                              className={`${styles.skeletonLine} ${styles.skeletonSmall}`}
                                          />
                                      </div>
                                  </div>
                              </article>
                          ))
                        : items.slice(0, 8).map((it, idx) => {
                              const bgRgb =
                                  themeMode === 'perCard'
                                      ? hexToRgb(it.themeHex) ?? singleRgb
                                      : singleRgb;

                              const textRgb = hexToRgb(it.textColor) ?? fallbackTextRgb;

                              const styleVars: React.CSSProperties = {
                                  ['--theme-rgb' as any]: `${bgRgb.r} ${bgRgb.g} ${bgRgb.b}`,
                                  ['--text-rgb' as any]: `${textRgb.r} ${textRgb.g} ${textRgb.b}`,
                              };

                              const href = it.href?.startsWith('/fr')
                                  ? it.href
                                  : `/fr${it.href}`;

                              return (
                                  <article
                                      key={it.id}
                                      className={styles.card}
                                      role="listitem"
                                      data-aos="fade-up"
                                      data-aos-delay={Math.min(idx * 40, 120)}
                                      style={styleVars}
                                  >
                                      <Link
                                          href={href}
                                          className={styles.link}
                                          aria-label={it.title}
                                      >
                                          <div className={styles.stack}>
                                              <div className={styles.media}>
                                                  <Image
                                                      src={it.imageSrc}
                                                      alt={it.imageAlt || it.title}
                                                      fill
                                                      className={styles.image}
                                                      sizes="(max-width: 700px) 100vw, (max-width: 1100px) 50vw, 560px"
                                                      priority={idx < 2}
                                                  />

                                                  {(it.tag || it.badgeSecondary) && (
                                                      <div className={styles.badges}>
                                                          {it.badgeSecondary && (
                                                              <div
                                                                  className={`${styles.tag} ${styles.secondaryTag}`}
                                                              >
                                                                  {it.badgeSecondary}
                                                              </div>
                                                          )}

                                                          {it.tag && (
                                                              <div className={styles.tag}>
                                                                  {it.tag}
                                                              </div>
                                                          )}
                                                      </div>
                                                  )}
                                              </div>

                                              <div className={styles.seam} aria-hidden="true">
                                                  <span className={styles.circleCta}>
                                                      <span className={styles.circleIcon}>→</span>
                                                  </span>
                                              </div>

                                              <div className={styles.textCard}>
                                                  <h3 className={styles.cardTitle}>
                                                      {it.title}
                                                  </h3>

                                                  <p className={styles.desc}>
                                                      {it.description}
                                                  </p>

                                                  <div className={styles.bottom}>
                                                      <span className={styles.session}>
                                                          {it.nextSession ??
                                                              dictionary?.general?.book ??
                                                              'Sur réservation'}
                                                      </span>
                                                  </div>
                                              </div>
                                          </div>

                                          <span className={styles.focusRing} aria-hidden="true" />
                                      </Link>
                                  </article>
                              );
                          })}
                </div>
            </Container>
        </section>
    );
}